import React, { useEffect } from 'react';
import classnames from 'classnames/bind';
import { Button } from '../../components/Button';
import { pagePathnames } from '../page-routes';
import { useDocumentTitle } from '../../shared/use-document-title';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import brainSrc from '../../assets/images/start/brain.png';
import umbrellaSrc from '../../assets/images/start/umbrella.png';
import fingerprintSrc from '../../assets/images/start/fingerprint-accepted.png';
import swissArmyKnifeSrc from '../../assets/images/start/swiss-army-knife.png';
import mainBgSrc from '../../assets/images/start/main-bg.png';
import blueBgSrc from '../../assets/images/start/blue-bg.jpg';
import aiSrc from '../../assets/images/start/ai.png';
import mobileAiSrc from '../../assets/images/start/ai-mob.png';
import learnSrc from '../../assets/images/start/learn.png';
import mobileLearnSrc from '../../assets/images/start/learn-mob.png';
import signsSrc from '../../assets/images/start/signs.png';
import mobileSignsSrc from '../../assets/images/start/signs-mob.png';
import breachSrc from '../../assets/images/start/breach.png';
import mobileBreachSrc from '../../assets/images/start/breach-mob.png';
import extensionSrc from '../../assets/images/start/extension.png';
import mobileExtensionSrc from '../../assets/images/start/extension-mob.png';
import chartLineSrc from '../../assets/images/start/chart-line.png';
import chartBarVerticalSrc from '../../assets/images/start/chart-bar-vertical.png';
import usersGroupSrc from '../../assets/images/start/users-group.png';
import styles from './StartPage.css';

const cx = classnames.bind(styles);

const ADVANTAGE_LIST = [
    {
        id: 1,
        title: 'AI-Powered Protection',
        description: '24/7 monitoring that blocks threats before they reach you. Our AI adapts in real time to new dangers.',
        imageUrl: brainSrc
    },
    {
        id: 2,
        title: 'Effortless Security',
        description: 'No jargon, no complex setup. Install quickly, let Futureproof run on autopilot, and stay protected with automatic updates.',
        imageUrl: umbrellaSrc
    },
    {
        id: 3,
        title: 'Personalized Safety Alerts',
        description: 'Get clear, timely notifications about suspicious logins, leaked passwords, and other risks.',
        imageUrl: fingerprintSrc
    },
    {
        id: 4,
        title: 'All-in-One Convenience',
        description: 'One subscription covers browsing protection, identity breach monitoring, and more.',
        imageUrl: swissArmyKnifeSrc
    }
];

const STATISTICS = [
    {
        id: 1,
        title: '$3.1B',
        description: 'That’s how much adults 60+ lost to online scams in 2022, according to the FBI.',
        imageUrl: chartLineSrc
    },
    {
        id: 2,
        title: '88,000+ cases',
        description: 'Adults 60+ experienced the highest number of reported cybercrime incidents among all age groups.',
        imageUrl: chartBarVerticalSrc
    },
    {
        id: 3,
        title: 'Common threats',
        description: 'Scams, fake tech support calls, romance scams, and identity theft.',
        imageUrl: usersGroupSrc
    }
]

function StartPage() {
    useEffect(() => {
        logEvent(EVENT_TYPES.HOMEPAGE_VISITED);
    }, []);

    useDocumentTitle('Futureproof');

    return (
        <div className={styles.root}>
            <main className={styles.main}>
                <div className={styles.wrapper}>
                    <section
                        className={cx('section', 'sectionWithBg')}
                        style={{['--main-bg']: `url(${mainBgSrc})`}}
                    >
                        <div className={styles.sectionWithBgWrapper}>
                            <h1 className={styles.title}>Your digital safety, simplified. AI-Driven Security Made Easy</h1>
                            <p className={styles.description}>Enjoy peace of mind with protection that runs quietly in the background, keeping you and your family safe.</p>
                            <Button
                                className={styles.button}
                                label='Start for Free'
                                link={{
                                    to: pagePathnames.signIn
                                }}
                            />
                            <p className={styles.smallNotice}>Create an account and get instant protection.</p>
                        </div>
                    </section>
                    <ul className={styles.serviceList}>
                        <li className={cx('serviceCard', 'serviceCardMedium')}>
                            <div className={styles.serviceCardContent}>
                                <h2 className={styles.serviceCardTitle}>AI Assistant</h2>
                                <p className={styles.serviceCardDescription}>Ask any cybersecurity-related questions — from checking suspicious messages to getting tips on data protection. The AI Assistant will provide quick and easy-to-understand advice.</p>
                            </div>
                            <div
                                className={styles.serviceCardImageBg}
                                style={{
                                    ['--image-bg']: `url(${aiSrc})`,
                                    ['--mobile-image-bg']: `url(${mobileAiSrc})`
                                }}
                            />
                        </li>
                        <li className={cx('serviceCard', 'serviceCardSmall')}>
                            <div className={styles.serviceCardContent}>
                                <h2 className={styles.serviceCardTitle}>Security Learning</h2>
                                <p className={styles.serviceCardDescription}>
                                    Explore simple guides and tips to learn how to protect yourself online. <br /><br />
                                    Perfect for beginners and anyone looking to improve their online security skills.
                                </p>
                            </div>
                            <div
                                className={styles.serviceCardImageBg}
                                style={{
                                    ['--image-bg']: `url(${learnSrc})`,
                                    ['--mobile-image-bg']: `url(${mobileLearnSrc})`
                                }}
                            />
                        </li>
                        <li className={cx('serviceCard', 'serviceCardSmall')}>
                            <div className={styles.serviceCardContent}>
                                <h2 className={styles.serviceCardTitle}>Link Reputation Checker</h2>
                                <p className={styles.serviceCardDescription}>
                                    Before visiting a website or opening an email, check its reputation. Make sure it’s safe and not listed in known threat databases.
                                </p>
                            </div>
                            <div
                                className={styles.serviceCardImageBg}
                                style={{
                                    ['--image-bg']: `url(${signsSrc})`,
                                    ['--mobile-image-bg']: `url(${mobileSignsSrc})`
                                }}
                            />
                        </li>
                        <li className={cx('serviceCard', 'serviceCardMedium')}>
                            <div className={styles.serviceCardContent}>
                                <h2 className={styles.serviceCardTitle}>Data Breach Analyzer</h2>
                                <p className={styles.serviceCardDescription}>Check if your personal information has been compromised. Get real-time alerts if your data appears in breach databases or on the dark web.</p>
                            </div>
                            <div
                                className={styles.serviceCardImageBg}
                                style={{
                                    ['--image-bg']: `url(${breachSrc})`,
                                    ['--mobile-image-bg']: `url(${mobileBreachSrc})`
                                }}
                            />
                        </li>
                        <li className={cx('serviceCard', 'serviceCardBig')}>
                            <div className={styles.serviceCardContent}>
                                <span className={styles.serviceCardTag}>Coming Soon</span>
                                <h2 className={styles.serviceCardTitle}>Browser Extension</h2>
                                <p className={styles.serviceCardDescription}>Install the extension for automatic protection against malicious websites, phishing attempts, and dangerous downloads while browsing online.</p>
                            </div>
                            <div
                                className={styles.serviceCardImageBg}
                                style={{
                                    ['--image-bg']: `url(${extensionSrc})`,
                                    ['--mobile-image-bg']: `url(${mobileExtensionSrc})`
                                }}
                            />
                        </li>
                    </ul>
                    <section className={styles.sectionAbout}>
                        <h2 className={styles.secondaryTitle}>Why Futureproof?</h2>
                        <p className={styles.description}>Futureproof is built for effortless security. Combining cutting-edge AI with a user-friendly experience, it keeps anyone safe online. Here’s why people choose Futureproof:</p>
                        <ul className={styles.advantageList}>
                            {ADVANTAGE_LIST.map(item => (
                                <li
                                    key={item.id}
                                    className={styles.advantageItem}
                                >
                                    <img
                                        src={item.imageUrl}
                                        alt={item.title}
                                        className={styles.advantageItemImage}
                                    />
                                    <p className={styles.advantageItemTitle}>{item.title}</p>
                                    <p className={styles.advantageItemDescription}>{item.description}</p>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            </main>
            <div className={styles.secondaryContent}>
                <div className={styles.wrapper}>
                    <section className={styles.sectionStatictics}>
                        <div className={styles.secondaryWrapper}>
                            <div className={styles.informationWrapper}>
                                <h2 className={styles.tertiaryTitle}>Digital Vulnerability Among Older Adults (U.S.)</h2>
                                <p className={styles.description}>Older adults are highly vulnerable to digital scams, highlighting the need for strong online security:</p>
                            </div>
                            <p className={styles.mark}>Futureproof is designed to make digital security effortless, especially for those less familiar with technology.</p>
                        </div>
                        <ul className={styles.statisticsList}>
                            {STATISTICS.map(item => (
                                <li
                                    key={item.id}
                                    className={styles.statisticCard}
                                >
                                    <div className={styles.statisticCardWrapper}>
                                        <img
                                            src={item.imageUrl}
                                            alt={item.title}
                                            className={styles.statisticCardImage}
                                        />
                                        <p className={styles.statisticCardTitle}>{item.title}</p>
                                    </div>
                                    <p className={styles.statisticCardDescription}>{item.description}</p>
                                </li>
                            ))}
                        </ul>
                    </section>
                    <section
                        className={styles.sectionReady}
                        style={{backgroundImage: `url(${blueBgSrc})`}}
                    >
                        <div className={styles.secondaryWrapper}>
                            <div className={styles.informationWrapper}>
                                <span className={styles.tag}>Try in now</span>
                                <h2 className={styles.tertiaryTitle}>Ready to Futureproof Your Digital Life?</h2>
                                <p className={styles.description}>Enjoy the internet without fear or hassle. Let Futureproof’s AI-driven security protect you and your family so you can browse, shop, and socialize with confidence.</p>
                            </div>
                            <div className={styles.buttons}>
                                <Button
                                    label='Start for Free'
                                    link={{
                                        to: pagePathnames.signIn
                                    }}
                                    className={styles.buttonStart}
                                />
                                <Button
                                    label='Learn more'
                                    type='secondary'
                                    link={{
                                        to: 'https://prelander.futureproof.app/v9',
                                        target: '_blank'
                                    }}
                                    iconRight={{
                                        name: 'arrowUpRight'
                                    }}
                                    className={styles.buttonLearn}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    )
};

export {
    StartPage
};
